import { Component, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { FormControl } from '@angular/forms';
import { DirectoryService } from '../../shared/directory.service';
import { DirectoryStatus } from '../../interfaces/directory';
import { Department } from '../../interfaces/department';
import { User } from '../../interfaces/user';
import { SubscribedComponent } from '../../subscribed.component';

interface ManagerRow { User_id: number; name: string; removing?: boolean; }

@Component({
  selector: 'bst-team-config-drawer',
  templateUrl: './team-config-drawer.component.html',
  styleUrls: ['./team-config-drawer.component.scss'],
  standalone: false,
})
export class TeamConfigDrawerComponent extends SubscribedComponent implements OnInit {
  // context (set by the opener)
  deptId!: number;
  deptName = '';
  parentId: number | null = null;

  // 0 sentinel: NbSelect's `selected` treats a bound null as "nothing selected" rather than matching a null-valued option (Department PKs start at 1, so 0 never collides).
  readonly NONE_PARENT = 0;

  get parentSelectValue(): number {
    return this.parentId ?? this.NONE_PARENT;
  }

  departments: Department[] = [];
  managers: ManagerRow[] = [];
  managersLoading = true;
  parentSaving = false;
  adding = false;

  picker = new FormControl<User | null>(null);

  constructor(public ref: NbDialogRef<TeamConfigDrawerComponent>,
              private directoryService: DirectoryService) { super(); }

  async ngOnInit(): Promise<void> {
    // Parent options: active departments in this tenant, minus self. Defensive to
    // both a bare array and a Paged<> shape (see Task 0 Step 2).
    this.directoryService.getDepartments(DirectoryStatus.ACTIVE).then((res: any) => {
      const list: Department[] = Array.isArray(res) ? res : (res?.data ?? []);
      this.departments = list.filter(d => d.id !== this.deptId);
    }).catch(() => { this.departments = []; });

    this.subs.push(this.picker.valueChanges.subscribe(u => { if (u) { this.addManager(u); } }));

    try {
      const rows = await this.directoryService.listManagers(this.deptId);
      this.managers = rows.map(m => ({ User_id: m.User_id, name: m.name }));
    } catch (e) {
      console.error('managers load failed', e);
    } finally {
      this.managersLoading = false;
    }
  }

  async onParentChange(newValue: number): Promise<void> {
    const newParent = newValue === this.NONE_PARENT ? null : newValue;
    const prev = this.parentId;
    this.parentId = newParent;      // optimistic: gives Angular a real newParent -> prev transition so the catch actually reverts the select
    this.parentSaving = true;
    try {
      await this.directoryService.setParent(this.deptId, newParent);
    } catch {
      this.parentId = prev;         // real value change -> NbSelect `selected` setter fires -> dropdown reverts
    } finally {
      this.parentSaving = false;
    }
  }

  private async addManager(u: User): Promise<void> {
    if (this.adding) { return; }
    const picked = u as { id: number; name?: string; name_first?: string; name_last?: string };
    if (this.managers.some(m => m.User_id === picked.id)) { this.picker.setValue(null, { emitEvent: false }); return; }
    this.adding = true;
    try {
      await this.directoryService.assignManager(this.deptId, picked.id);
      const name = (picked.name ?? `${picked.name_first ?? ''} ${picked.name_last ?? ''}`).trim();
      this.managers = [...this.managers, { User_id: picked.id, name }];
    } catch { /* errorify already toasted */ }
    finally { this.adding = false; this.picker.setValue(null, { emitEvent: false }); }
  }

  async removeManager(row: ManagerRow): Promise<void> {
    if (row.removing) { return; }
    row.removing = true;
    try {
      const res = await this.directoryService.removeManager(this.deptId, row.User_id);
      if (res && res.removed) {
        this.managers = this.managers.filter(m => m.User_id !== row.User_id);
        return;
      }
    } catch (e) {
      console.error('manager remove failed', e);
    }
    row.removing = false;                     // refused or failed (toasted) -> keep the row
  }

  close(): void { this.ref.close(); }
}
